/**
 * Nutriment PDP — Dog Size Feeding Brackets
 * Experiment: EXPBA2CC25E  |  Variation: 1
 *
 * Adds a dog-weight SLIDER to the product page covering nine weight bands.
 * Each band sizes a full 30-day supply of this product. The pack size is read from
 * the product title so the maths is correct on every pack size. Feeding rule is
 * Nutriment's own headline guidance from their raw feeding calculator: adult dogs eat
 * ~30g of food per KG of bodyweight per day (3% of bodyweight). Package count, total
 * price and price-per-day are computed from the LIVE product price so they stay accurate.
 *
 * Choosing a band sets the product's own QUANTITY box to the right number of tubs.
 * The shopper then uses the site's existing "Add to basket" button to add to cart —
 * this variant does NOT add its own add-to-cart buttons.
 *
 * Each band is sized at the TOP of its range so the quantity is always enough for a
 * 30-day supply and never underfeeds (e.g. 0-5 kg -> sized @ 5 kg -> 9 tubs;
 * 40 kg + -> sized @ 45 kg -> 81 tubs).
 *
 * Fonts and surfaces reuse the theme's heading/body scale and product-card tokens so
 * they match the rest of the site exactly.
 */
(function () {
  "use strict";

  var EXPERIMENT_ID = "EXPBA2CC25E";
  var VARIATION_ID = "1";

  // --- Feeding + product constants (the only hardcoded values) ---
  var GRAMS_PER_KG_PER_DAY = window.packSizeChooserConfig.gramsPerKgPerDay; // Nutriment adult-dog headline: 30g per kg per day (3% bodyweight)
  var DAYS = 30; // "30-day supply"
  var DEFAULT_TUB_GRAMS = 500; // fallback pack size if the title doesn't state one
  var FALLBACK_PRICE_PENCE = 299; // £2.99 — used only if live price can't be read

  // Pack size in grams, read from the product title (e.g. "Raw Chicken 1.4kg" -> 1400,
  // "... 500g tub" -> 500) so the maths is right on every pack size, not just the 500g tub.
  var TUB_GRAMS = DEFAULT_TUB_GRAMS;

  // Parse a pack weight out of a product title. Returns grams, or null if none found.
  function parsePackGrams(title) {
    if (!title) return null;
    var t = String(title).toLowerCase();
    // Kilograms first (e.g. "1.4kg", "1,4 kg"), then grams (e.g. "500g").
    var kg = t.match(/(\d+(?:[.,]\d+)?)\s*kg\b/);
    if (kg) {
      var kgVal = parseFloat(kg[1].replace(",", "."));
      if (!isNaN(kgVal) && kgVal > 0) return Math.round(kgVal * 1000);
    }
    var g = t.match(/(\d+(?:[.,]\d+)?)\s*g\b/);
    if (g) {
      var gVal = parseFloat(g[1].replace(",", "."));
      if (!isNaN(gVal) && gVal > 0) return Math.round(gVal);
    }
    return null;
  }

  // Resolve the pack size from the rendered product title.
  function resolveTubGrams(productInfo) {
    try {
      var titleEl =
        (productInfo && productInfo.querySelector(".product__title h1")) ||
        document.querySelector(".product__title h1") ||
        (productInfo && productInfo.querySelector("h1"));
      var grams = parsePackGrams(titleEl && titleEl.textContent);
      if (grams) return grams;
    } catch (e) {
      /* fall through */
    }
    return DEFAULT_TUB_GRAMS;
  }

  // Human label for the pack size, e.g. 500 -> "500g", 1400 -> "1.4kg".
  function formatPackSize(grams) {
    if (grams >= 1000) {
      var kg = grams / 1000;
      return (kg % 1 === 0 ? kg.toFixed(0) : String(kg)) + "kg";
    }
    return grams + "g";
  }

  // Bracket definitions. sizeKg = the weight used to size the 30-day supply (top of range),
  // so the quantity is always enough and never underfeeds. Presented as a slider because
  // there are too many bands to show as tappable boxes.
  var DOG_BRACKETS = [
    { key: "kg0_5", range: "0 – 5 kg", sizeKg: 5 },
    { key: "kg5_10", range: "5 – 10 kg", sizeKg: 10 },
    { key: "kg10_15", range: "10 – 15 kg", sizeKg: 15 },
    { key: "kg15_20", range: "15 – 20 kg", sizeKg: 20 },
    { key: "kg20_25", range: "20 – 25 kg", sizeKg: 25 },
    { key: "kg25_30", range: "25 – 30 kg", sizeKg: 30 },
    { key: "kg30_35", range: "30 – 35 kg", sizeKg: 35 },
    { key: "kg35_40", range: "35 – 40 kg", sizeKg: 40 },
    { key: "kg40plus", range: "40 kg +", sizeKg: 45 },
  ];

  // Cat brackets: 1 kg increments up to 9 kg (top-of-range sizing per band).
  var CAT_BRACKETS = [
    { key: "kg1", range: "1 kg", sizeKg: 1 },
    { key: "kg2", range: "2 kg", sizeKg: 2 },
    { key: "kg3", range: "3 kg", sizeKg: 3 },
    { key: "kg4", range: "4 kg", sizeKg: 4 },
    { key: "kg5", range: "5 kg", sizeKg: 5 },
    { key: "kg6", range: "6 kg", sizeKg: 6 },
    { key: "kg7", range: "7 kg", sizeKg: 7 },
    { key: "kg8", range: "8 kg", sizeKg: 8 },
    { key: "kg9", range: "9 kg", sizeKg: 9 },
  ];

  var BRACKETS =
    window.packSizeChooserConfig.animal === "cat" ? CAT_BRACKETS : DOG_BRACKETS;

  function reportFailure(reason, selector, description, errorMessage) {
    try {
      if (typeof Variant !== "undefined" && Variant.reportExperienceFailure) {
        Variant.reportExperienceFailure(
          reason,
          selector,
          EXPERIMENT_ID,
          VARIATION_ID,
          description,
          errorMessage,
        );
      }
    } catch (e) {
      /* never throw from the reporter */
    }
  }

  function publish(eventName, extra) {
    try {
      if (typeof Variant !== "undefined" && Variant.publishCustomEvent) {
        var payload = {
          experiment_id: EXPERIMENT_ID,
          variation_id: VARIATION_ID,
        };
        if (extra) {
          for (var k in extra) {
            if (extra.hasOwnProperty(k)) payload[k] = extra[k];
          }
        }
        Variant.publishCustomEvent(eventName, payload);
      }
    } catch (e) {
      /* swallow */
    }
  }

  // Resolve an identifier for this product so picks can be attributed to it.
  // Preferred: the variant id the buy form actually submits. Falls back to the
  // product json node, then the product handle from the path.
  function getProductId() {
    try {
      // Prefer the real PRODUCT id. The buy form carries it as a hidden 'product-id'
      // field; Shopify's analytics meta is the next best source. Note that the form's
      // 'id' field is the VARIANT id, so it is only a last resort.
      var pidInput =
        document.querySelector(
          'form[action*="/cart/add"] input[name="product-id"]',
        ) || document.querySelector('input[name="product-id"]');
      if (pidInput && pidInput.value) return String(pidInput.value);
      if (
        window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.id
      ) {
        return String(window.ShopifyAnalytics.meta.product.id);
      }
      var idInput =
        document.querySelector('form[action*="/cart/add"] input[name="id"]') ||
        document.querySelector('input.product-variant-id[name="id"]') ||
        document.querySelector('input[name="id"]');
      if (idInput && idInput.value) return String(idInput.value);
      var json = document.querySelector('[id^="ProductJSON-"]');
      if (json) {
        var fromJson = json.id.replace("ProductJSON-", "");
        if (fromJson) return fromJson;
      }
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      if (m && m[1]) return m[1];
    } catch (e) {
      /* fall through */
    }
    return "";
  }

  // ---- Money helpers ----
  function formatGBP(pence) {
    return "£" + (pence / 100).toFixed(2);
  }

  // Read the live single-variant price (in pence) from the rendered price node.
  function getLivePricePence(productInfo) {
    try {
      var priceEl = productInfo.querySelector(
        ".price__sale .price-item--sale, .price__regular .price-item--regular, .price-item--sale, .price-item--regular",
      );
      if (priceEl) {
        var val = parseFloat(
          (priceEl.textContent || "").replace(/[^0-9.]/g, ""),
        );
        if (!isNaN(val) && val > 0) return Math.round(val * 100);
      }
    } catch (e) {
      /* fall through */
    }
    return FALLBACK_PRICE_PENCE;
  }

  // Compute the per-bracket numbers from live price.
  function computeBracket(b, pricePence) {
    var dailyGrams = b.sizeKg * GRAMS_PER_KG_PER_DAY;
    var totalGrams = dailyGrams * DAYS;
    var tubs = Math.ceil(totalGrams / TUB_GRAMS);
    var totalPence = tubs * pricePence;
    var perDayPence = totalPence / DAYS;
    return {
      dailyGrams: dailyGrams,
      tubs: tubs,
      totalPence: totalPence,
      perDayPence: perDayPence,
      totalGrams: tubs * TUB_GRAMS,
    };
  }

  // ---- Styles: match the theme's REAL type scale. The site's body copy is 1.5rem
  //      (15px) and headings use the heading scale (h3 = 1.7rem, h4 = 1.5rem). We size
  //      everything to those values so nothing looks smaller than the rest of the page.
  //      Each card shows only name + weight range + price-per-day (equal height); the
  //      full breakdown (total price, tubs, supply, grams/day) appears below the grid,
  //      only for the currently selected size. ----
  function injectStyles() {
    if (document.getElementById("v-feeding-brackets-styles")) return;
    var css =
      "" +
      ".v-feed-brackets{margin:2rem 0;font-family:inherit;}" +
      // Section title: h3-level heading (matches the product page's own sub-headings).
      ".v-feed-brackets__title{font-family:var(--font-heading-family);font-size:calc(var(--font-heading-scale,1)*1.7rem);font-weight:var(--font-heading-weight,600);margin:2rem 0 1.4rem;color:rgb(var(--color-foreground));line-height:calc(1 + 0.3 / var(--font-body-scale,1));}" +
      // ---- Dog-size slider. Sits in a card-styled surface matching the theme's product cards.
      ".v-feed-slider{background:var(--nutriment-background-color, rgba(var(--color-foreground),0.03));" +
      "border:var(--product-card-border-width,0.2rem) solid rgba(var(--color-foreground),var(--product-card-border-opacity,1));" +
      "border-radius:var(--product-card-corner-radius,2rem);padding:1.6rem 1.8rem 1.4rem;box-sizing:border-box;" +
      "transition:border-color .15s ease, box-shadow .15s ease;}" +
      // Once a band has been chosen the surface picks up the brand frame, matching the tub option.
      ".v-feed-slider[data-active]{border-color:var(--v-accent);box-shadow:inset 0 0 0 0.1rem var(--v-accent);}" +
      // Current band, shown large above the track.
      ".v-feed-slider__readout{text-align:center;margin-bottom:1rem;}" +
      ".v-feed-slider__value{font-family:var(--font-heading-family);font-size:calc(var(--font-heading-scale,1)*1.6rem);font-weight:var(--font-heading-weight,600);color:rgb(var(--color-foreground));line-height:1.2;}" +
      // The track: filled portion uses the brand colour, remainder is a muted foreground tint.
      ".v-feed-slider__input{-webkit-appearance:none;appearance:none;width:100%;height:2.4rem;margin:0;padding:0;background:transparent;display:block;cursor:pointer;}" +
      ".v-feed-slider__input:focus{outline:none;}" +
      ".v-feed-slider__input:focus-visible{outline:0.2rem solid var(--v-accent);outline-offset:0.4rem;border-radius:var(--inputs-radius,3rem);}" +
      ".v-feed-slider__input::-webkit-slider-runnable-track{height:0.6rem;border-radius:0.3rem;" +
      "background:linear-gradient(to right, var(--v-accent) 0%, var(--v-accent) var(--v-fill,0%), rgba(var(--color-foreground),0.2) var(--v-fill,0%), rgba(var(--color-foreground),0.2) 100%);}" +
      ".v-feed-slider__input::-moz-range-track{height:0.6rem;border-radius:0.3rem;background:rgba(var(--color-foreground),0.2);}" +
      ".v-feed-slider__input::-moz-range-progress{height:0.6rem;border-radius:0.3rem;background:var(--v-accent);}" +
      // The thumb: large enough to be an easy touch target on mobile.
      ".v-feed-slider__input::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:2.4rem;height:2.4rem;border-radius:50%;" +
      "background:var(--v-accent);border:0.3rem solid rgb(var(--color-background,255 255 255));box-shadow:0 0 0 0.1rem rgba(0,0,0,0.15);margin-top:-0.9rem;cursor:pointer;}" +
      ".v-feed-slider__input::-moz-range-thumb{width:2.4rem;height:2.4rem;border-radius:50%;" +
      "background:var(--v-accent);border:0.3rem solid rgb(var(--color-background,255 255 255));box-shadow:0 0 0 0.1rem rgba(0,0,0,0.15);cursor:pointer;}" +
      // Min/max captions under the track.
      ".v-feed-slider__scale{display:flex;justify-content:space-between;gap:1rem;margin-top:0.6rem;font-size:1.3rem;color:rgba(var(--color-foreground),0.6);letter-spacing:0.04rem;line-height:1.3;}" +
      // Base card styled to match the theme's product cards: solid product-card border
      // (width + full opacity foreground) and the product-card background/corner radius.
      ".v-feed-card{cursor:pointer;background:var(--nutriment-background-color, rgba(var(--color-foreground),0.03));" +
      "border:var(--product-card-border-width,0.2rem) solid rgba(var(--color-foreground),var(--product-card-border-opacity,1));" +
      "border-radius:var(--product-card-corner-radius,2rem);padding:1.6rem;color:inherit;display:flex;flex-direction:column;justify-content:flex-start;" +
      "gap:0.4rem;width:100%;box-sizing:border-box;transition:border-color .15s ease, box-shadow .15s ease, background-color .15s ease;}" +
      ".v-feed-card:hover{border-color:rgb(var(--color-foreground));}" +
      ".v-feed-card:focus-visible{outline:0.2rem solid rgb(var(--color-foreground));outline-offset:0.2rem;}" +
      // Selected card: orange (brand) frame + ring only (no background tint).
      '.v-feed-card[aria-pressed="true"]{border-color:var(--v-accent);box-shadow:inset 0 0 0 0.1rem var(--v-accent);}' +
      // Full-width "single tub" option that sits above the grid. Declared AFTER the base
      // .v-feed-card rule so its row layout overrides the base column layout.
      ".v-feed-card--single{flex-direction:row;align-items:center;justify-content:space-between;gap:1.2rem;margin-bottom:1.2rem;}" +
      ".v-feed-card__col{display:flex;flex-direction:column;gap:0.2rem;text-align:left;}" +
      ".v-feed-card__sub{font-size:1.3rem;letter-spacing:0.04rem;color:rgba(var(--color-foreground),0.6);line-height:1.3;margin-top:0.2rem;}" +
      ".v-feed-card__price{font-family:var(--font-heading-family);font-size:calc(var(--font-heading-scale,1)*1.6rem);font-weight:700;color:rgb(var(--color-foreground));white-space:nowrap;}" +
      // Right-aligned price column on the single option: price with a per-kg line beneath it.
      ".v-feed-card__pricecol{display:flex;flex-direction:column;align-items:flex-end;gap:0.2rem;text-align:right;}" +
      ".v-feed-card__perkg{font-size:1.3rem;color:rgba(var(--color-foreground),0.6);font-weight:400;letter-spacing:0.04rem;line-height:1.3;white-space:nowrap;}" +
      // Weight-range label: h4-level heading (1.5rem scaled) so it reads as the card's primary heading.
      ".v-feed-card__range{font-family:var(--font-heading-family);font-size:calc(var(--font-heading-scale,1)*1.5rem);font-weight:var(--font-heading-weight,600);color:rgb(var(--color-foreground));line-height:1.2;}" +
      // Details panel below the grid — only shows the selected option. Clean neutral surface.
      ".v-feed-details{margin:1.4rem 0 0;padding:0;max-height:0;overflow:hidden;opacity:0;border-radius:var(--product-card-corner-radius,2rem);transition:max-height .25s ease, opacity .2s ease, padding .25s ease;}" +
      ".v-feed-details.is-open{max-height:44rem;opacity:1;padding:2rem;background:rgba(var(--color-foreground),0.04);border:0.1rem solid rgba(var(--color-foreground),0.18);}" +
      // Lead sentence: normal body copy — this is the explanation the shopper reads first.
      ".v-feed-details__lead{font-size:1.5rem;color:rgba(var(--color-foreground),0.85);margin:0;letter-spacing:0.04rem;line-height:calc(1 + 0.5 / var(--font-body-scale,1));}" +
      ".v-feed-details__lead b{color:rgb(var(--color-foreground));font-weight:600;}" +
      // Price block sits under the sentence, separated by a thin divider.
      ".v-feed-details__pricewrap{margin:1.4rem 0 0;padding:1.4rem 0 0;border-top:0.1rem solid rgba(var(--color-foreground),0.12);}" +
      ".v-feed-details__price{font-family:var(--font-heading-family);font-size:calc(var(--font-heading-scale,1)*2.6rem);font-weight:700;color:rgb(var(--color-foreground));line-height:1.05;}" +
      ".v-feed-details__perday{font-size:1.4rem;color:rgba(var(--color-foreground),0.6);font-weight:400;letter-spacing:0.04rem;margin-top:0.4rem;}";
    var style = document.createElement("style");
    style.id = "v-feeding-brackets-styles";
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Find the product's own quantity input (linked to the buy form via the form= attribute).
  function getQuantityInput(productInfo) {
    return (
      (productInfo &&
        productInfo.querySelector('input.quantity__input[name="quantity"]')) ||
      document.querySelector('input.quantity__input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  // ---- Build the brackets UI ----
  function buildUI(productInfo) {
    var pricePence = getLivePricePence(productInfo);
    // Read the real pack size for THIS product before any of the maths runs.
    TUB_GRAMS = resolveTubGrams(productInfo);

    var wrap = document.createElement("section");
    wrap.className = "v-feed-brackets";
    wrap.setAttribute("data-v-feed-brackets", "1");
    var isCat = window.packSizeChooserConfig.animal === "cat";
    var animalLabel = isCat ? "Cat" : "Dog";
    wrap.setAttribute(
      "aria-label",
      "Choose your " + animalLabel.toLowerCase() + " size for a 30-day supply",
    );
    // Selected-option frame color: prefer theme button color, fall back to brand orange.
    wrap.style.setProperty("--v-accent", "rgb(var(--color-button, 255 164 0))");

    var title = document.createElement("h3");
    title.className = "v-feed-brackets__title h3";
    title.textContent = "Or, Choose " + animalLabel + " Size";
    wrap.appendChild(title);

    // Details panel below the slider — only shows the currently selected size.
    var details = document.createElement("div");
    details.className = "v-feed-details";
    details.setAttribute("aria-live", "polite");

    // Clear the selected state on every option (single tub + slider) so only one is active.
    function clearSelection() {
      var all = wrap.querySelectorAll(".v-feed-card");
      for (var i = 0; i < all.length; i++)
        all[i].setAttribute("aria-pressed", "false");
      var sl = wrap.querySelector(".v-feed-slider");
      if (sl) sl.removeAttribute("data-active");
    }

    // Set the product's own quantity box to the given number of tubs. Returns false if not found.
    function setQuantity(tubs) {
      var qty = getQuantityInput(productInfo);
      if (!qty) {
        reportFailure(
          "element_not_found",
          'input.quantity__input[name="quantity"]',
          "set the quantity box from a dog-size feeding option so the site's Add to basket button adds the right number of tubs",
        );
        return false;
      }
      qty.value = tubs;
      // Fire the events the theme's quantity component listens for.
      qty.dispatchEvent(new Event("input", { bubbles: true }));
      qty.dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    // Append the selected option's total price to the "Add to basket" button so
    // it reads e.g. "Add to basket - £26.91". The button's own base label is kept
    // (captured once) so re-selecting never stacks the price on itself.
    function setBuyButtonPrice(pence) {
      var buttons = document.querySelectorAll(".product-form__submit");
      if (!buttons.length) {
        reportFailure(
          "element_not_found",
          ".product-form__submit",
          "add the total price to the Add to basket button label",
        );
        return;
      }
      for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        // The button holds its label inside a span; fall back to the button itself.
        var labelEl = btn.querySelector("span") || btn;
        // Skip the sold-out / disabled button so we never relabel it with a price.
        if (btn.disabled) continue;
        var base = labelEl.getAttribute("data-v-feed-baselabel");
        if (base === null) {
          base = (labelEl.textContent || "").trim();
          labelEl.setAttribute("data-v-feed-baselabel", base);
        }
        labelEl.textContent = base + " - " + formatGBP(pence);
      }
    }

    // Update the details panel for a chosen size bracket. The panel leads with a
    // plain-language explanation of what we're offering, then the price and the
    // per-day cost underneath. Deliberately no other figures — the whole point is
    // that the shopper has one sentence and one price to take in.
    function renderDetails(b, calc, qtySet) {
      details.innerHTML =
        "" +
        '<p class="v-feed-details__lead">We suggest a ' +
        DAYS +
        " day supply of <b>" +
        calc.tubs +
        " packs</b> at this weight</p>" +
        '<div class="v-feed-details__pricewrap">' +
        '<div class="v-feed-details__price">' +
        formatGBP(calc.totalPence) +
        "</div>" +
        '<div class="v-feed-details__perday">' +
        formatGBP(calc.perDayPence) +
        " / day</div>" +
        "</div>";
      details.classList.add("is-open");
    }

    // The overview panel is only shown for dog-size options. When the single
    // "Single Tray" option is selected, hide (close) the panel entirely.
    function hideDetails() {
      details.classList.remove("is-open");
      details.innerHTML = "";
    }

    // ---- Full-width "single tub" option, above the grid ----
    var single = document.createElement("div");
    single.className = "v-feed-card v-feed-card--single";
    single.setAttribute("role", "button");
    single.setAttribute("tabindex", "0");
    single.setAttribute("aria-pressed", "false");
    single.setAttribute("data-bracket", "single");
    var singleCol = document.createElement("div");
    singleCol.className = "v-feed-card__col";
    var singleTitle = document.createElement("div");
    singleTitle.className = "v-feed-card__range h4";
    singleTitle.textContent = "Single Pack";
    var singleSub = document.createElement("div");
    singleSub.className = "v-feed-card__sub";
    singleSub.textContent = "One " + formatPackSize(TUB_GRAMS) + " pack to try";
    singleCol.appendChild(singleTitle);
    singleCol.appendChild(singleSub);
    var singlePriceCol = document.createElement("div");
    singlePriceCol.className = "v-feed-card__pricecol";
    var singlePrice = document.createElement("div");
    singlePrice.className = "v-feed-card__price";
    singlePrice.textContent = formatGBP(pricePence);
    // Per-kg price, derived from the pack weight so it tracks the live price.
    var perKgPence = Math.round(pricePence * (1000 / TUB_GRAMS));
    var singlePerKg = document.createElement("div");
    singlePerKg.className = "v-feed-card__perkg";
    singlePerKg.textContent = formatGBP(perKgPence) + " / kg";
    singlePriceCol.appendChild(singlePrice);
    singlePriceCol.appendChild(singlePerKg);
    single.appendChild(singleCol);
    single.appendChild(singlePriceCol);
    function selectSingle() {
      clearSelection();
      single.setAttribute("aria-pressed", "true");
      var ok = setQuantity(1);
      hideDetails();
      setBuyButtonPrice(pricePence);
      // Forget the committed band so tapping that same band again re-applies it.
      committedKey = null;
      // Adding a single pack is not a dog-size bundle, so it must not be attributed.
      selectedForCart = null;
      // Selection is still tracked, but the single tub is deliberately NOT
      // attributed revenue — only the multi-tub dog-size packages are.
      publish("feeding_bracket_selected", { bracket: "single", quantity: 1 });
    }
    single.addEventListener("click", selectSingle);
    single.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectSingle();
      }
    });
    // Place the single-tub option ABOVE the "Or, Choose Dog Size" title.
    wrap.insertBefore(single, title);

    // ---- Dog-size SLIDER (replaces the old tap-boxes: too many bands to show as boxes) ----
    // A native range input drives the choice: real touch/drag behaviour, arrow-key support
    // and screen-reader semantics for free. The current band's label sits above it.
    var sliderWrap = document.createElement("div");
    sliderWrap.className = "v-feed-slider";

    // The currently-highlighted weight band, shown large above the track.
    var readout = document.createElement("div");
    readout.className = "v-feed-slider__readout";
    var readoutValue = document.createElement("span");
    readoutValue.className = "v-feed-slider__value h4";
    readoutValue.textContent = BRACKETS[0].range;
    readout.appendChild(readoutValue);
    sliderWrap.appendChild(readout);

    var slider = document.createElement("input");
    slider.type = "range";
    slider.className = "v-feed-slider__input";
    slider.min = "0";
    slider.max = String(BRACKETS.length - 1);
    slider.step = "1";
    slider.value = "0";
    slider.setAttribute("aria-label", "Your dog's weight");
    slider.setAttribute("aria-valuetext", BRACKETS[0].range);
    sliderWrap.appendChild(slider);

    // Min/max captions so the range is readable at a glance.
    var scale = document.createElement("div");
    scale.className = "v-feed-slider__scale";
    var scaleMin = document.createElement("span");
    scaleMin.textContent = BRACKETS[0].range;
    var scaleMax = document.createElement("span");
    scaleMax.textContent = BRACKETS[BRACKETS.length - 1].range;
    scale.appendChild(scaleMin);
    scale.appendChild(scaleMax);
    sliderWrap.appendChild(scale);

    // Paint the filled portion of the track up to the thumb.
    function paintTrack() {
      var max = BRACKETS.length - 1;
      var pct = max > 0 ? (Number(slider.value) / max) * 100 : 0;
      slider.style.setProperty("--v-fill", pct + "%");
    }

    // Update only the label/track while dragging — cheap, no quantity writes.
    function previewBracket() {
      var b = BRACKETS[Number(slider.value)] || BRACKETS[0];
      readoutValue.textContent = b.range;
      slider.setAttribute("aria-valuetext", b.range);
      paintTrack();
    }

    // Remembers what is currently committed, so a single tap that fires several events
    // (e.g. 'change' then 'pointerup' then 'click') only commits and tracks once.
    var committedKey = null;

    // The dog-size band currently chosen via the slider, or null when the shopper is on
    // the single-pack option. Only a non-null value gets revenue-attributed on add-to-cart.
    var selectedForCart = null;

    // Commit the chosen band: set quantity, open the summary, update the button, track it.
    function commitBracket() {
      var b = BRACKETS[Number(slider.value)] || BRACKETS[0];
      // Always refresh the visible state — cheap, and keeps the frame/label in sync even
      // when the single-tub option was picked in between without the slider moving.
      clearSelection();
      sliderWrap.setAttribute("data-active", "1");
      previewBracket();
      // Everything below changes cart state or reports analytics, so run it only when
      // this is genuinely a new selection.
      if (committedKey === b.key) return;
      committedKey = b.key;
      var calc = computeBracket(b, pricePence);
      var ok = setQuantity(calc.tubs);
      renderDetails(b, calc, ok);
      setBuyButtonPrice(calc.totalPence);
      publish("feeding_bracket_selected", {
        bracket: b.key,
        quantity: calc.tubs,
      });
      // NOTE: revenue attribution is deliberately NOT fired here. Merely sliding through
      // bands is not intent to buy — it would over-count every shopper who explores the
      // slider and leaves. Attribution fires on add-to-cart instead (see trackAddToCart).
      // Remember the current pick so the add-to-cart handler can report it.
      selectedForCart = {
        bracket: b.key,
        quantity: calc.tubs,
        totalPence: calc.totalPence,
      };
    }

    // 'input' fires continuously while dragging; 'change' fires when released, but ONLY
    // if the value actually changed. That alone would make the first band (0 - 5 kg)
    // unselectable, because the slider already sits on it — tapping it changes nothing,
    // so no event fires. So we also commit on release/tap of the control itself, which
    // covers "tap the track at the first band" and "drag and return to where you started".
    slider.addEventListener("input", previewBracket);
    slider.addEventListener("change", commitBracket);
    // Pointer events cover mouse + touch + pen on modern browsers; the touch/click pair
    // is the fallback for anything that doesn't support them.
    if (window.PointerEvent) {
      slider.addEventListener("pointerup", commitBracket);
    } else {
      slider.addEventListener("touchend", commitBracket);
      slider.addEventListener("mouseup", commitBracket);
    }
    // Keyboard users get 'change' from arrow keys; this also catches assistive tech
    // that activates the control without moving it.
    slider.addEventListener("click", commitBracket);
    paintTrack();

    // ---- Revenue attribution on ADD TO CART ----
    // Fires only when the shopper actually adds a dog-size bundle to the basket, which is
    // the real intent signal. The theme adds to cart over AJAX (no page navigation), so the
    // redirect-safe pattern is not needed here. We listen in the CAPTURE phase so our event
    // is published before the theme's own submit handler runs.
    // Guards against the same add being reported twice when both the button click and the
    // form submit fire for one action. Reset on a short timer so the next add is counted.
    var atcReportedAt = 0;

    function trackAddToCart() {
      // Nothing chosen via the slider (shopper is on the single-pack option) -> not ours.
      if (!selectedForCart) return;
      // De-duplicate: one shopper action can raise both click and submit.
      var now = new Date().getTime();
      if (now - atcReportedAt < 1200) return;
      atcReportedAt = now;
      var pick = selectedForCart;
      // Product-level attribution: links this basket add to this product for 14-day
      // purchase attribution.
      publish("product-clicked-attribution", {
        product_id: getProductId(),
        bracket: pick.bracket,
        quantity: pick.quantity,
        total_price: (pick.totalPence / 100).toFixed(2),
      });
      // Also record the interaction as influenced revenue for the experiment overall.
      publish("influenced-revenue", {
        bracket: pick.bracket,
        quantity: pick.quantity,
        total_price: (pick.totalPence / 100).toFixed(2),
      });
      publish("dog_size_bundle_added_to_cart", {
        bracket: pick.bracket,
        quantity: pick.quantity,
        total_price: (pick.totalPence / 100).toFixed(2),
      });
    }

    // Bind at DOCUMENT level in the capture phase rather than on a specific form.
    // This theme renders the buy button once and then MOVES it between a desktop
    // container and a sticky mobile container (on window.onload and on every resize),
    // and there can be more than one buy form in the DOM. Binding to a single form we
    // happen to find at build time is unreliable: the form may not exist yet, or the
    // shopper may tap a different one. Delegating from the document covers every form,
    // however it is moved, and whenever it appears.
    if (!document.documentElement.hasAttribute("data-v-feed-atc-bound")) {
      document.documentElement.setAttribute("data-v-feed-atc-bound", "1");
      document.addEventListener(
        "submit",
        function (e) {
          var form = e.target;
          if (!form || form.nodeName !== "FORM") return;
          // Only the add-to-cart form. Uses a contains match because the preview proxy
          // rewrites the action URL to an absolute proxied path.
          var action = form.getAttribute("action") || "";
          if (
            action.indexOf("/cart/add") === -1 &&
            form.getAttribute("data-type") !== "add-to-cart-form"
          )
            return;
          trackAddToCart();
        },
        true,
      );

      // Safety net: also catch a tap on the add-to-cart button itself, in case the theme
      // ever adds to the basket without raising a form submit. The de-duplication guard
      // in trackAddToCart keeps a single tap from being reported twice.
      document.addEventListener(
        "click",
        function (e) {
          var t = e.target;
          if (!t || !t.closest) return;
          var btn = t.closest('.product-form__submit, button[name="add"]');
          if (!btn || btn.disabled) return;
          trackAddToCart();
        },
        true,
      );
    }

    wrap.appendChild(sliderWrap);
    wrap.appendChild(details);

    // Pre-select the single "Single Tray" option so the page opens with a valid
    // choice (sets the product's quantity box to 1). The overview panel stays
    // hidden for this option. No selection event is fired here — that only
    // tracks deliberate user choices.
    single.setAttribute("aria-pressed", "true");
    setQuantity(1);
    setBuyButtonPrice(pricePence);

    return wrap;
  }

  // ---- Hide the theme's native PRICE at the top of the product info ----
  // The live price has already been read in buildUI, so hiding it here is safe.
  // We hide only the price itself and keep the "Frozen" badge visible, since the
  // badge lives inside the same price container in the theme.
  function hideNativePrice(productInfo) {
    var priceBlock =
      productInfo.querySelector('[id^="price-"]') ||
      productInfo.querySelector(".price");
    if (!priceBlock) {
      reportFailure(
        "element_not_found",
        '[id^="price-"]',
        "hide the theme's native price at the top of the product page",
      );
      return;
    }
    // Preferred: hide just the price element(s) inside the container so the
    // frozen badge (a sibling) stays in place exactly where it was.
    var priceEls = priceBlock.matches(".price")
      ? [priceBlock]
      : priceBlock.querySelectorAll(".price");
    if (priceEls && priceEls.length) {
      for (var i = 0; i < priceEls.length; i++) {
        priceEls[i].style.setProperty("display", "none", "important");
      }
      // Also hide any other direct children that are NOT the frozen badge
      // (e.g. tax note wrappers rendered alongside the price), but only within
      // the price container itself — never the frozen tag.
      return;
    }
    // Fallback: no dedicated price element found. Hide everything in the
    // container except the frozen badge so the badge is preserved.
    var kids = priceBlock.children;
    var hidAny = false;
    for (var j = 0; j < kids.length; j++) {
      if (kids[j].classList && kids[j].classList.contains("frozen__tag"))
        continue;
      kids[j].style.setProperty("display", "none", "important");
      hidAny = true;
    }
    if (!hidAny) {
      priceBlock.style.setProperty("display", "none", "important");
    }
  }

  // ---- Hide the theme's native QUANTITY selector + its "Quantity" label ----
  // The picker drives the quantity behind the scenes, so the shopper no longer
  // needs the stepper. We hide the whole quantity block visually but keep the
  // input in the DOM (it stays wired to the buy form so Add to basket still adds
  // the right number of trays).
  function hideQuantityUI(productInfo) {
    var qtyBlock =
      productInfo.querySelector("#Variant-Quantity-Container") ||
      productInfo.querySelector(".product-form__quantity");
    if (!qtyBlock) {
      reportFailure(
        "element_not_found",
        "#Variant-Quantity-Container",
        "hide the native quantity selector and its label",
      );
      return;
    }
    qtyBlock.style.setProperty("display", "none", "important");
  }

  // ---- Hide the theme's "Not sure how much raw to feed?" prompt ----
  // Matched by its text (its position in the markup is not stable), scoped to the
  // product info so we never touch the same words elsewhere on the page.
  function hideRawFeedPrompt(productInfo) {
    var target = "not sure how much raw to feed";
    var ps = productInfo.querySelectorAll("p");
    for (var i = 0; i < ps.length; i++) {
      var txt = (ps[i].textContent || "").trim().toLowerCase();
      if (txt.indexOf(target) === 0) {
        ps[i].style.setProperty("display", "none", "important");
        // Remove the top padding on the rich-text block that holds this prompt
        // (and the calculator link), so the block sits tighter under the button.
        var block =
          ps[i].closest(".product__text-padding") || ps[i].parentElement;
        if (block) block.style.setProperty("padding-top", "0", "important");
        return;
      }
    }
    reportFailure(
      "element_not_found",
      'p:contains("Not sure how much raw to feed?")',
      'hide the "Not sure how much raw to feed?" prompt on the product page',
    );
  }

  // ---- Apply: find a stable anchor and insert once ----
  function apply(productInfo) {
    if (!productInfo || productInfo.getAttribute("data-v-feed-applied") === "1")
      return true;

    injectStyles();
    var ui;
    try {
      ui = buildUI(productInfo);
    } catch (e) {
      reportFailure(
        "mutation_failed",
        "product-info.product__info-container",
        "build the dog-size feeding brackets on the product page",
        e && e.message ? e.message : String(e),
      );
      return false;
    }

    // Preferred anchor: just before the quantity/buy area. Fall back to appending.
    var anchor =
      productInfo.querySelector("#Variant-Quantity-Container") ||
      productInfo.querySelector(".product-form__quantity") ||
      productInfo.querySelector("#BuyButtonDesktopContainer") ||
      productInfo.querySelector("product-form");

    try {
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(ui, anchor);
      } else {
        var grid1 = productInfo.querySelector(".grid-1") || productInfo;
        grid1.appendChild(ui);
      }
    } catch (e) {
      reportFailure(
        "mutation_failed",
        "product-info.product__info-container",
        "insert the dog-size feeding brackets into the product page",
        e && e.message ? e.message : String(e),
      );
      return false;
    }

    productInfo.setAttribute("data-v-feed-applied", "1");
    hideNativePrice(productInfo);
    hideQuantityUI(productInfo);
    hideRawFeedPrompt(productInfo);
    reportExposure();
    return true;
  }

  // ---- Exposure: fire when the product info (where the brackets live) is in view ----
  var exposureFired = false;
  function reportExposure() {
    if (exposureFired) return;
    var anchor =
      document.querySelector("product-info.product__info-container") ||
      document.querySelector('[id^="ProductInfo-"]');
    if (!anchor) return;
    try {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            if (
              !exposureFired &&
              typeof Variant !== "undefined" &&
              Variant.reportExposure
            ) {
              exposureFired = true;
              Variant.reportExposure(
                EXPERIMENT_ID,
                VARIATION_ID,
                "viewport",
                "product info with dog-size feeding brackets scrolled into view",
              );
            }
            io.disconnect();
            return;
          }
        }
      });
      io.observe(anchor);
    } catch (e) {
      /* non-critical */
    }
  }

  // ---- Boot: find product-info, wait for late render, guard everything ----
  function start() {
    var root = document.querySelector('[id^="ProductInfo-"]')
      ? document
      : document.body || document.documentElement;
    if (!root) return;

    function tryApply() {
      var productInfo =
        document.querySelector("product-info.product__info-container") ||
        document.querySelector('[id^="ProductInfo-"]');
      if (!productInfo) return false;
      return apply(productInfo);
    }

    if (tryApply()) return;

    var observed = document.body || document.documentElement;
    if (!observed) return;
    var obs = new MutationObserver(function () {
      if (tryApply()) obs.disconnect();
    });
    obs.observe(observed, { childList: true, subtree: true });

    // Stop watching after 15s; report if the product info never appeared.
    setTimeout(function () {
      obs.disconnect();
      if (!document.querySelector("[data-v-feed-brackets]")) {
        reportFailure(
          "timeout",
          "product-info.product__info-container",
          "wait for the product info block so we can add the dog-size feeding brackets",
        );
      }
    }, 15000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
